import Phaser from 'phaser';

/**
 * Плавающий под потолком враг — патрулирует по горизонтали. Каждые 6 секунд
 * сбрасывает {@link Orb}. Сцена слушает событие 'dropper-emit'.
 */
export class EnemyDropper extends Phaser.Physics.Arcade.Sprite {
  private hp = 18;
  private hurtTimer = 0;
  private dying = false;
  private dropTimer = 4000;
  private dir: 1 | -1 = 1;
  private patrolMin: number;
  private patrolMax: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    patrolMin: number,
    patrolMax: number,
  ) {
    super(scene, x, y, 'enemy-dropper-hq');
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setDisplaySize(120, 130);

    this.patrolMin = patrolMin;
    this.patrolMax = patrolMax;

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(72, 60);
    body.setOffset((this.width - 72) / 2, this.height * 0.15);
    body.setAllowGravity(false);
    body.setVelocityX(45);
    body.setMaxVelocity(60, 60);

    scene.tweens.add({
      targets: this,
      y: y - 6,
      duration: 1400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    });
  }

  override preUpdate(time: number, delta: number) {
    super.preUpdate(time, delta);
    if (this.dying) return;
    this.hurtTimer = Math.max(0, this.hurtTimer - delta);
    this.dropTimer -= delta;

    const body = this.body as Phaser.Physics.Arcade.Body;
    if (this.x <= this.patrolMin) this.dir = 1;
    if (this.x >= this.patrolMax) this.dir = -1;
    body.setVelocityX(this.dir * 45);
    this.setFlipX(this.dir === -1);

    if (this.dropTimer <= 0) {
      this.dropTimer = 6000;
      this.scene.events.emit('dropper-emit', { x: this.x, y: this.y + 24 });
    }
  }

  takeDamage(amount: number, knockbackX: number) {
    if (this.dying) return;
    this.hp -= amount;
    this.hurtTimer = 200;
    this.setTintFill(0xffffff);
    this.scene.time.delayedCall(80, () => {
      if (this.active && !this.dying) this.clearTint();
    });
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setVelocityX(knockbackX);
    if (this.hp <= 0) this.die();
  }

  private die() {
    this.dying = true;
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setEnable(false);
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scaleX: 0.5,
      scaleY: 0.5,
      duration: 280,
      onComplete: () => this.destroy(),
    });
  }

  isAlive() {
    return !this.dying && this.active;
  }
}

/**
 * Светящийся орб без Phaser physics body — управляется полностью вручную:
 * гравитация и падение в `manualTick`, столкновение с полом и игроком — через
 * простую проверку координат. Это полностью исключает любые проблемы с
 * arcade-collider/overlap, которые ранее вызывали зависание.
 */
export class Orb extends Phaser.GameObjects.Image {
  private vy = 220;
  private dead = false;
  private pulseTween?: Phaser.Tweens.Tween;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, 'orb');
    scene.add.existing(this);
    this.setDisplaySize(28, 28);

    this.pulseTween = scene.tweens.add({
      targets: this,
      scaleX: this.scaleX * 1.15,
      scaleY: this.scaleY * 1.15,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    });
  }

  /** Вызывается из GameScene.update — гравитация + падение. */
  manualTick(delta: number) {
    if (this.dead) return;
    const dt = delta / 1000;
    this.vy = Math.min(360, this.vy + 1050 * dt);
    this.y += this.vy * dt;
  }

  popHarmless() {
    if (this.dead) return;
    this.dead = true;
    if (this.pulseTween) {
      this.pulseTween.stop();
      this.pulseTween = undefined;
    }
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scaleX: this.scaleX * 1.6,
      scaleY: this.scaleY * 0.4,
      duration: 180,
      ease: 'Quad.out',
      onComplete: () => this.destroy(),
    });
  }

  explode() {
    if (this.dead) return;
    this.dead = true;
    if (this.pulseTween) {
      this.pulseTween.stop();
      this.pulseTween = undefined;
    }

    const flash = this.scene.add.graphics();
    flash.setDepth(this.depth + 1);
    flash.setPosition(this.x, this.y);
    this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 220,
      onUpdate: (tw) => {
        const t = tw.getValue() ?? 0;
        const r = 6 + t * 36;
        flash.clear();
        flash.fillStyle(0xff6aa8, 0.45 * (1 - t));
        flash.fillCircle(0, 0, r);
        flash.lineStyle(2, 0xff90c0, 0.9 * (1 - t));
        flash.strokeCircle(0, 0, r);
      },
      onComplete: () => flash.destroy(),
    });

    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scaleX: this.scaleX * 1.6,
      scaleY: this.scaleY * 1.6,
      duration: 160,
      onComplete: () => this.destroy(),
    });
  }

  isAlive() {
    return !this.dead && this.active;
  }

  override destroy(fromScene?: boolean) {
    if (this.pulseTween) {
      this.pulseTween.stop();
      this.pulseTween = undefined;
    }
    super.destroy(fromScene);
  }
}
